import * as React from 'react';
import { Link } from 'react-router-dom';
import { AppContext, AppContextAccount } from './AppContext';
import { getAuthorityName } from './components/AuthorityLabel';
import { LogoutButton } from './components/LogoutButton';
import { APP_NAME } from './Const';
import './stylesheets/Header.css';

const HeaderLogo: React.FC = function () {
  return (
    <Link className="Header-logo" to="/">
      <span className="Header-icon App-icon">
        <img alt="App Icon" src="/appicon.png" />
      </span>
      <span className="Header-title">{APP_NAME}</span>
    </Link>
  );
};

interface HeaderUserMenuProps {
  companyName: string;
  me: AppContextAccount;
}

/**
 * ヘッダー右上の「ログアウト」ボタン。standard と同じく右上に常に表示する。押したときの動きはマイページのボタンと同じ
 */
const HeaderLogoutButton: React.FC = function () {
  return (
    <div className="Header-logout">
      <LogoutButton />
    </div>
  );
};

/**
 * ヘッダー右上の利用者の表示。押すと、利用者の情報とマイページへの移動を並べたメニューを開く。
 * メニューは、メニューの外側を押したときとEscキーを押したときに閉じる。
 */
const HeaderUserMenu: React.FC<HeaderUserMenuProps> = function (props) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const handleMouseDown = function (event: MouseEvent) {
      const container = containerRef.current;
      if (container && event.target instanceof Node && container.contains(event.target)) {
        return;
      }
      setOpen(false);
    };

    const handleKeyDown = function (event: KeyboardEvent) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  let menu;
  if (open) {
    menu = (
      <div className="Header-user_menu" role="menu">
        <div className="Header-user_menu_profile">
          <div className="Header-user_menu_name">{props.me.name} さん</div>
          <div className="Header-user_menu_company">{props.companyName}</div>
          <div className="Header-user_menu_authority">
            {getAuthorityName(props.me.authority.level)}
          </div>
        </div>
        <Link
          className="Header-user_menu_item"
          role="menuitem"
          to="/account"
          onClick={() => setOpen(false)}
        >
          マイページ
        </Link>
      </div>
    );
  }

  return (
    <div className="Header-account" ref={containerRef}>
      <button
        type="button"
        className="Header-account_button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span>{props.companyName}</span>
        <span className="Header-account_separator">·</span>
        <span className="Header-account_name">{props.me.name}</span>
        <span className="Header-account_san">さん</span>
        <span className="Header-account_caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {menu}
    </div>
  );
};

/**
 * アプリケーションのヘッダー
 */
export const Header: React.FC = function () {
  const appContext = React.useContext(AppContext);
  const appInfo = appContext.state.appInfo;
  const me = appContext.state.me;

  if (!appInfo) {
    return (
      <header className="Header-header">
        <HeaderLogo />
      </header>
    );
  }

  if (!me) {
    return (
      <header className="Header-header">
        <HeaderLogo />
        <div className="Header-account">
          <span className="Header-company_name">{appInfo.companyName}</span>
        </div>
      </header>
    );
  }

  return (
    <header className="Header-header">
      <HeaderLogo />
      <HeaderUserMenu companyName={appInfo.companyName} me={me} />
      <HeaderLogoutButton />
    </header>
  );
};
